/**
 * PopulationSegmentContext — V2.1
 *
 * Gestiona el estado de visualización de población por segmento.
 * Soporta dos métodos coexistentes:
 *   - census_network_length (default, V1.7)
 *   - building_refined (V2.0)
 *
 * V2.1: Agrega selectedMethod + setSelectedMethod + runBuildingRefinedCalculation.
 * Al cambiar de método, recarga status + GeoJSON para el método seleccionado.
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import {
  populationSegmentApi,
  type PopulationMethod,
  type PopulationSegmentStatus,
  type PopulationSegmentFeatureProperties,
  type PopulationSegmentRunStatus,
} from '../services/population-segment-api.service';
import { useProject } from '../../projects';

// ============================================================================
// TYPES
// ============================================================================

export type { PopulationMethod };

/**
 * Variables de visualización disponibles.
 * Las variables `*_per_km` se calculan en frontend; las otras son contables.
 */
export type PopulationVariable =
  | 'population_per_km'
  | 'population'
  | 'households_per_km'
  | 'households'
  | 'nbi_households_per_km'
  | 'nbi_households';

export interface PopulationVariableRange {
  min: number;
  max: number;
  p95: number;
}

export interface PopulationSegmentContextValue {
  // ── Método activo ──
  selectedMethod: PopulationMethod;
  setSelectedMethod: (m: PopulationMethod) => void;

  // ── Estado del run ──
  runStatus: PopulationSegmentRunStatus;
  runMetadata: PopulationSegmentStatus | null;
  isLoading: boolean;
  isCalculating: boolean;
  errorMessage: string | null;

  // ── Datos de edges ──
  edgeData: Map<string, PopulationSegmentFeatureProperties>;
  ranges: Record<PopulationVariable, PopulationVariableRange | null>;

  // ── Visualización ──
  selectedVariable: PopulationVariable;
  populationModeActive: boolean;

  // ── Acciones ──
  setSelectedVariable: (v: PopulationVariable) => void;
  setPopulationModeActive: (active: boolean) => void;
  runCalculation: () => Promise<void>;
  runBuildingRefinedCalculation: () => Promise<void>;
  reloadData: () => Promise<void>;
}

// ============================================================================
// HELPERS
// ============================================================================

function computeRange(values: number[]): PopulationVariableRange | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const p95Index = Math.floor(sorted.length * 0.95);
  const p95 = sorted[Math.min(p95Index, sorted.length - 1)];
  return { min, max, p95 };
}

function toKm(lengthM: number | null | undefined): number | null {
  if (lengthM == null || lengthM <= 0) return null;
  return lengthM / 1000;
}

function buildRanges(
  data: Map<string, PopulationSegmentFeatureProperties>,
): Record<PopulationVariable, PopulationVariableRange | null> {
  const popAbs: number[] = [];
  const popKm: number[] = [];
  const hhAbs: number[] = [];
  const hhKm: number[] = [];
  const nbiAbs: number[] = [];
  const nbiKm: number[] = [];

  for (const props of data.values()) {
    // network_length_m es null para building_refined → fallback a length_meters (de canonical_edges)
    const km = toKm(props.network_length_m ?? props.length_meters);
    if (props.population_estimated != null) {
      popAbs.push(props.population_estimated);
      if (km != null) popKm.push(props.population_estimated / km);
    }
    if (props.households_estimated != null) {
      hhAbs.push(props.households_estimated);
      if (km != null) hhKm.push(props.households_estimated / km);
    }
    if (props.nbi_households_estimated != null) {
      nbiAbs.push(props.nbi_households_estimated);
      if (km != null) nbiKm.push(props.nbi_households_estimated / km);
    }
  }

  return {
    population_per_km:     computeRange(popKm),
    population:            computeRange(popAbs),
    households_per_km:     computeRange(hhKm),
    households:            computeRange(hhAbs),
    nbi_households_per_km: computeRange(nbiKm),
    nbi_households:        computeRange(nbiAbs),
  };
}

const EMPTY_RANGES: Record<PopulationVariable, PopulationVariableRange | null> = {
  population_per_km: null, population: null,
  households_per_km: null, households: null,
  nbi_households_per_km: null, nbi_households: null,
};

// ============================================================================
// CONTEXT
// ============================================================================

const PopulationSegmentContext = createContext<PopulationSegmentContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

export interface PopulationSegmentProviderProps {
  children: ReactNode;
}

export function PopulationSegmentProvider({ children }: PopulationSegmentProviderProps): JSX.Element {
  const { projectId } = useProject();

  const currentProjectIdRef = useRef<string | null>(null);
  const dataLoadedForProjectRef = useRef<string | null>(null);
  const pollingIntervalRef = useRef<number | null>(null);

  // ── Estado ──
  const [selectedMethod, setSelectedMethodState] = useState<PopulationMethod>('census_network_length');
  const [runStatus, setRunStatus] = useState<PopulationSegmentRunStatus>('no_runs');
  const [runMetadata, setRunMetadata] = useState<PopulationSegmentStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ── Datos ──
  const [edgeData, setEdgeData] = useState<Map<string, PopulationSegmentFeatureProperties>>(new Map());
  const [ranges, setRanges] = useState<Record<PopulationVariable, PopulationVariableRange | null>>(EMPTY_RANGES);

  // ── Visualización ──
  const [selectedVariable, setSelectedVariable] = useState<PopulationVariable>('population_per_km');
  const [populationModeActive, setPopulationModeActive] = useState(false);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current !== null) {
      window.clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const clearData = useCallback(() => {
    setEdgeData(new Map());
    setRanges({ ...EMPTY_RANGES });
  }, []);

  /**
   * Carga el GeoJSON del método indicado y construye el Map de edges.
   */
  const loadGeoJson = useCallback(async (pid: string, method: PopulationMethod): Promise<void> => {
    try {
      const geojson = await populationSegmentApi.getGeoJson(pid, method);
      if (currentProjectIdRef.current !== pid) return;

      const map = new Map<string, PopulationSegmentFeatureProperties>();
      for (const feature of geojson.features) {
        map.set(feature.properties.edge_id, feature.properties);
      }
      setEdgeData(map);
      setRanges(buildRanges(map));
      console.log('[PopulationSegmentContext] GeoJSON loaded:', map.size, 'edges for project:', pid, 'method:', method);
    } catch (e) {
      if (currentProjectIdRef.current !== pid) return;
      console.error('[PopulationSegmentContext] Error loading GeoJSON:', e);
    }
  }, []);

  /**
   * Carga el estado del último run y, si completado, el GeoJSON.
   */
  const reloadData = useCallback(async (): Promise<void> => {
    if (!projectId) return;
    const pid = projectId;

    setIsLoading(true);
    setErrorMessage(null);

    // Capture current method at call time to avoid stale closure issues
    const method = selectedMethod;

    try {
      const status = await populationSegmentApi.getStatus(pid, method);
      if (currentProjectIdRef.current !== pid) return;

      setRunMetadata(status);
      setRunStatus(status.status);

      if (status.status === 'completed') {
        await loadGeoJson(pid, method);
      } else if (status.status === 'failed') {
        setErrorMessage(status.errorMessage ?? 'El cálculo falló sin mensaje de error.');
        clearData();
      } else {
        clearData();
      }
    } catch (e) {
      if (currentProjectIdRef.current !== pid) return;
      console.error('[PopulationSegmentContext] Error loading status:', e);
      setErrorMessage(e instanceof Error ? e.message : 'Error al cargar el estado.');
    } finally {
      if (currentProjectIdRef.current === pid) {
        setIsLoading(false);
      }
    }
  }, [projectId, selectedMethod, loadGeoJson, clearData]);

  /**
   * Polling: consulta el estado cada 4 s hasta completado/fallido.
   */
  const startPolling = useCallback((pid: string, method: PopulationMethod): void => {
    stopPolling();

    let attempts = 0;
    const maxAttempts = 75;

    pollingIntervalRef.current = window.setInterval(async () => {
      if (currentProjectIdRef.current !== pid) { stopPolling(); return; }

      attempts++;
      if (attempts > maxAttempts) {
        stopPolling();
        setIsCalculating(false);
        setErrorMessage('El cálculo está tardando más de lo esperado. Recargá para ver el estado.');
        return;
      }

      try {
        const status = await populationSegmentApi.getStatus(pid, method);
        if (currentProjectIdRef.current !== pid) { stopPolling(); return; }

        setRunMetadata(status);
        setRunStatus(status.status);

        if (status.status === 'completed') {
          stopPolling();
          setIsCalculating(false);
          await loadGeoJson(pid, method);
          console.log('[PopulationSegmentContext] Calculation completed — data loaded, method:', method);
        } else if (status.status === 'failed') {
          stopPolling();
          setIsCalculating(false);
          setErrorMessage(status.errorMessage ?? 'El cálculo falló.');
        }
      } catch (e) {
        console.error('[PopulationSegmentContext] Polling error:', e);
      }
    }, 4000);
  }, [stopPolling, loadGeoJson]);

  /**
   * Lanza un nuevo cálculo base (census_network_length).
   */
  const runCalculation = useCallback(async (): Promise<void> => {
    if (!projectId || isCalculating) return;
    const pid = projectId;

    try {
      setIsCalculating(true);
      setErrorMessage(null);
      clearData();

      const result = await populationSegmentApi.runBase(pid);
      if (currentProjectIdRef.current !== pid) return;

      console.log('[PopulationSegmentContext] Base run launched:', result.runId);
      setRunStatus('running');
      startPolling(pid, 'census_network_length');
    } catch (e) {
      if (currentProjectIdRef.current !== pid) return;
      setIsCalculating(false);
      setErrorMessage(e instanceof Error ? e.message : 'Error al lanzar el cálculo.');
    }
  }, [projectId, isCalculating, clearData, startPolling]);

  /**
   * Lanza un nuevo cálculo refinado edilicio (building_refined).
   */
  const runBuildingRefinedCalculation = useCallback(async (): Promise<void> => {
    if (!projectId || isCalculating) return;
    const pid = projectId;

    try {
      setIsCalculating(true);
      setErrorMessage(null);
      clearData();

      const result = await populationSegmentApi.runBuildingRefined(pid);
      if (currentProjectIdRef.current !== pid) return;

      console.log('[PopulationSegmentContext] Building refined run launched:', result.runId);
      setRunStatus('running');
      startPolling(pid, 'building_refined');
    } catch (e) {
      if (currentProjectIdRef.current !== pid) return;
      setIsCalculating(false);
      setErrorMessage(e instanceof Error ? e.message : 'Error al lanzar el cálculo refinado.');
    }
  }, [projectId, isCalculating, clearData, startPolling]);

  /**
   * Cambia el método activo y recarga datos para el nuevo método.
   */
  const setSelectedMethod = useCallback((m: PopulationMethod): void => {
    if (!projectId) return;
    setSelectedMethodState(m);
    // Forzar recarga para el nuevo método
    dataLoadedForProjectRef.current = null;
  }, [projectId]);

  // ── Resetear al cambiar de proyecto ──
  useEffect(() => {
    currentProjectIdRef.current = projectId;
    dataLoadedForProjectRef.current = null;
    stopPolling();

    setSelectedMethodState('census_network_length');
    setRunStatus('no_runs');
    setRunMetadata(null);
    setIsLoading(false);
    setIsCalculating(false);
    setErrorMessage(null);
    clearData();
    setPopulationModeActive(false);
  }, [projectId, stopPolling, clearData]);

  // ── Cargar datos cuando se activa el modo o cambia el método ──
  useEffect(() => {
    if (
      populationModeActive &&
      projectId &&
      dataLoadedForProjectRef.current !== `${projectId}:${selectedMethod}` &&
      !isLoading
    ) {
      dataLoadedForProjectRef.current = `${projectId}:${selectedMethod}`;
      console.log('[PopulationSegmentContext] Loading data for:', projectId, 'method:', selectedMethod);
      void reloadData();
    }
  }, [populationModeActive, projectId, selectedMethod, isLoading, reloadData]);

  // ── Cleanup al desmontar ──
  useEffect(() => {
    return () => { stopPolling(); };
  }, [stopPolling]);

  const value: PopulationSegmentContextValue = {
    selectedMethod,
    setSelectedMethod,
    runStatus,
    runMetadata,
    isLoading,
    isCalculating,
    errorMessage,
    edgeData,
    ranges,
    selectedVariable,
    populationModeActive,
    setSelectedVariable,
    setPopulationModeActive,
    runCalculation,
    runBuildingRefinedCalculation,
    reloadData,
  };

  return (
    <PopulationSegmentContext.Provider value={value}>
      {children}
    </PopulationSegmentContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

export function usePopulationSegment(): PopulationSegmentContextValue {
  const context = useContext(PopulationSegmentContext);
  if (!context) {
    throw new Error('usePopulationSegment must be used within PopulationSegmentProvider');
  }
  return context;
}

export function usePopulationSegmentSafe(): PopulationSegmentContextValue | null {
  return useContext(PopulationSegmentContext);
}
